import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import 'wicg-inert';

// Guard against double initialization
if (window.__NAV_INIT__) {
	console.warn('[nav] already initialized — skipping');
} else {
	window.__NAV_INIT__ = true;
	// ============ DEBUG HELPERS ============
	// Trace output only with ?navdebug in the URL. The former page-wide 'error' and
	// 'unhandledrejection' listeners re-logged every error on the page — including
	// third-party tags' opaque "Script error." — as this bundle's own console errors.
	const NAV_DEBUG = /[?&]navdebug\b/.test(window.location.search);
	const DBG = (...a) => { if (NAV_DEBUG) console.log('[nav]', ...a); };
	const WARN = (...a) => console.warn('[nav]', ...a);
	const ERR = (...a) => console.error('[nav]', ...a);

	// ============ HELPERS ============
	function safeInert(el, val) {
		if (!el) return;
		try {
			el.inert = !!val;
		} catch (_) {}
	}

	const lockedEls = new Set();
	function lock(el) {
		if (!el || lockedEls.has(el)) return;
		try {
			disableBodyScroll(el, { reserveScrollBarGap: true });
			lockedEls.add(el);
		} catch (e) {
			WARN('lock failed', e);
		}
	}
	function unlockAll() {
		lockedEls.forEach((el) => {
			try {
				enableBodyScroll(el);
			} catch (e) {
				WARN('unlock failed', e);
			}
		});
		lockedEls.clear();
	}

	function getEl(selector, ctx = document, required = true) {
		const el = ctx.querySelector(selector);
		if (!el && required) WARN('missing element', selector);
		return el || null;
	}
	function getAll(selector, ctx = document) {
		return Array.from(ctx.querySelectorAll(selector) || []);
	}

	// ============ MAIN INITIALIZER ============
	function initNav() {
		if (document.querySelector('[data-amt-header]')) return;
		DBG('DOM ready');

		const navBar = getEl('.nav-bar');
		if (!navBar) return WARN('No .nav-bar found, aborting nav init');

		const navBarLinks = navBar.querySelectorAll('a');
		const locations = navBar.querySelectorAll('.nav-province-link');
		const navToggle = getAll('[data-toggle-nav]');
		const dropBtns = getAll('[data-toggle-subnav]');
		const mainMenu = getEl('.nav-main', document, false);
		const main = getEl('#main', document, false);
		const footer = getEl('#footer', document, false);
		const mediaQuery = window.matchMedia('(min-width: 1024px)');

		if (NAV_DEBUG) console.table({
			navBar: !!navBar,
			mainMenu: !!mainMenu,
			navToggle: navToggle.length,
			dropBtns: dropBtns.length,
			links: navBarLinks.length,
			locations: locations.length,
		});

		// ---------- Dropdowns ----------
		function closeOpenItems(current) {
			try {
				document.querySelectorAll('.dropdown').forEach((menu) => {
					if (menu !== current) {
						menu.classList.remove('show');
						const link = menu.querySelector('.link');
						if (link) link.setAttribute('aria-expanded', 'false');
					}
				});
				if (mainMenu) mainMenu.classList.remove('open');
			} catch (e) {
				ERR('closeOpenItems', e);
			}
		}

		// ---------- Swirl animation ----------
		const DRAW_LINK = '.header-nav-article-link[data-draw]';

		function resetAllSwirls(scope = mainMenu) {
			if (!scope) return;
			scope
				.querySelectorAll(DRAW_LINK)
				.forEach((a) => a.classList.remove('is-active'));
			scope.querySelectorAll(`${DRAW_LINK} svg`).forEach((svg) => {
				svg.classList.remove('is-active');
				svg.style.animation = 'none';
				svg.offsetWidth; // reflow
				svg.style.animation = '';
				svg.style.opacity = '';
			});
		}

		function activateSwirl(linkEl) {
			if (!linkEl) return;
			const localScope = linkEl.closest('.dropdown .content') || mainMenu;
			resetAllSwirls(localScope);
			linkEl.classList.add('is-active');
			const svg = linkEl.querySelector('svg');
			if (!svg) return;
			svg.classList.add('is-active');
			svg.style.opacity = '1';
			svg.style.animation = 'none';
			svg.offsetWidth; // reflow
			svg.style.animation = '';
		}

		if (mainMenu) {
			mainMenu.addEventListener('mouseover', (e) => {
				const link = e.target.closest(DRAW_LINK);
				if (link) activateSwirl(link);
			});
			mainMenu.addEventListener('mouseleave', () => resetAllSwirls());
			mainMenu.addEventListener('focusin', (e) => {
				const link = e.target.closest(DRAW_LINK);
				if (link) activateSwirl(link);
			});
			mainMenu.addEventListener('focusout', () => {
				if (!mainMenu.contains(document.activeElement))
					resetAllSwirls();
			});
		}

		const _closeOpenItems = closeOpenItems;
		closeOpenItems = function (current) {
			_closeOpenItems(current);
			resetAllSwirls();
		};

		function closeMobileMenu() {
			try {
				navBar.classList.remove('open');
				safeInert(main, false);
				safeInert(footer, false);
				closeOpenItems();
				unlockAll();
				navToggle.forEach((btn) =>
					btn.setAttribute('aria-expanded', 'false')
				);
				DBG('mobile menu closed');
			} catch (e) {
				ERR('closeMobileMenu', e);
			}
		}

		// ---------- Bindings ----------
		navBarLinks.forEach((link) => {
			link.addEventListener('click', () => {
				DBG('nav link clicked -> closing menus');
				closeOpenItems();
			});
		});

		locations.forEach((button) => {
			button.addEventListener('click', () => {
				DBG('province clicked -> closing menus');
				closeOpenItems();
			});
		});

		dropBtns.forEach((btn) => {
			btn.addEventListener('click', (event) => {
				try {
					event.preventDefault();
					const dropDown = btn.closest('.dropdown');
					if (!dropDown)
						return WARN('no .dropdown ancestor for subnav button');

					closeOpenItems(dropDown);
					dropDown.classList.toggle('show');

					const expanded =
						btn.getAttribute('aria-expanded') === 'true';
					btn.setAttribute('aria-expanded', String(!expanded));

					const isOpen = mainMenu
						? mainMenu.querySelectorAll('.show').length
						: 0;
					if (mainMenu) mainMenu.classList.toggle('open', !!isOpen);

					DBG('subnav toggled; open?', !!isOpen);
				} catch (e) {
					ERR('dropBtns click', e);
				}
			});
		});

		navToggle.forEach((btn) => {
			btn.addEventListener('click', (event) => {
				try {
					event.preventDefault();

					const navMenu = navBar.querySelector('.scroller');
					const dropdownMenu =
						navBar.querySelectorAll('.dropdown .content');

					if (!navBar.classList.contains('open')) {
						navBar.classList.add('open');
						btn.setAttribute('aria-expanded', 'true');
						safeInert(main, true);
						safeInert(footer, true);
						if (navMenu) lock(navMenu);
						dropdownMenu.forEach((elem) => lock(elem));
						DBG('mobile menu opened');
					} else {
						closeMobileMenu();
					}
				} catch (e) {
					ERR('navToggle click', e);
				}
			});
		});

		window.addEventListener('click', (event) => {
			try {
				if (!event.target.closest('.dropdown')) {
					closeOpenItems();
				}
			} catch (e) {
				ERR('window click', e);
			}
		});

		document.addEventListener('keyup', (event) => {
			try {
				if (event.key === 'Escape' || event.keyCode === 27) {
					DBG('ESC pressed -> closing');
					closeOpenItems();
					closeMobileMenu();
				}
			} catch (err) {
				ERR('keyHandler', err);
			}
		});

		window.addEventListener('resize', () => {
			try {
				if (mediaQuery.matches) {
					DBG('>=1024px -> closing mobile nav');
					closeMobileMenu();
				}
			} catch (e) {
				ERR('resizeHandler', e);
			}
		});

		DBG('nav initialized ✅');
	}

	// Run immediately if DOM is already ready, otherwise wait
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initNav);
	} else {
		initNav();
	}
}
